import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Tooltip,
  TablePagination,
  TableSortLabel,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Divider,
  Badge,
} from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import FilterListIcon from '@mui/icons-material/FilterList';
import moment from 'moment';

const useStyles = makeStyles(() => ({
  tabsRoot: {
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#fff',
  },
  tab: {
    minWidth: 140,
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '0.9rem',
  },
  badge: {
    marginLeft: 8,
    height: 20,
    fontSize: '0.7rem',
    fontWeight: 700,
    color: '#fff',
  },
  // Grouped table styles
  tableContainer: {
    height: '100%',
    overflow: 'auto',
    borderRadius: 0,
    boxShadow: 'none',
    border: '1px solid #e0e0e0',
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  tableHeadCell: {
    fontWeight: 700,
    fontSize: '0.78rem',
    color: '#424242',
    padding: '4px',
    whiteSpace: 'normal',
    wordBreak: 'keep-all',
    lineHeight: 1.25,
    verticalAlign: 'bottom',
    borderRight: '1px solid #e0e0e0',
    '&:last-child': { borderRight: 'none' },
  },
  headerInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    width: '100%',
  },
  headerIcons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  sortLabel: {
    flex: 1,
    minWidth: 0,
    fontSize: 'inherit',
    fontWeight: 'inherit',
    color: 'inherit',

    '& .MuiTableSortLabel-icon': {
      fontSize: '0.9rem',
    },
  },
  filterBtn: {
    padding: 1,
    flexShrink: 0,
  },
  primaryRow: {
    backgroundColor: '#fff',
    '&:hover': { backgroundColor: '#f9f9f9' },
    cursor: 'default',
  },
  primaryRowRetest: {
    backgroundColor: '#fff8e1',
    '&:hover': { backgroundColor: '#fff3cd' },
  },
  historyRow: {
    backgroundColor: '#fafafa',
    '&:hover': { backgroundColor: '#f5f5f5' },
  },
  historyRowRetest: {
    backgroundColor: '#fff8e1',
    opacity: 0.75,
    '&:hover': { backgroundColor: '#fff3cd' },
  },
  tableCell: {
    fontSize: '0.82rem',
    padding: '6px 8px',
    borderRight: '1px solid #eeeeee',
    '&:last-child': { borderRight: 'none' },
  },
  historyCellIndented: {
    fontSize: '0.8rem',
    color: '#666',
    padding: '5px 12px',
    borderRight: '1px solid #eeeeee',
    '&:last-child': { borderRight: 'none' },
  },
  expandCell: {
    width: 40,
    padding: '0 4px',
    borderRight: '1px solid #eeeeee',
  },
  docketBtn: {
    color: '#1976d2',
    fontWeight: 700,
    fontSize: '0.82rem',
    padding: '2px',
    minWidth: 'unset',
    textTransform: 'none',
  },
  testNoBadge: {
    display: 'inline-block',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    borderRadius: 10,
    padding: '1px 8px',
    fontSize: '0.75rem',
    fontWeight: 700,
  },
  testNoBadgeOld: {
    display: 'inline-block',
    backgroundColor: '#f5f5f5',
    color: '#757575',
    borderRadius: 10,
    padding: '1px 8px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  retestNoBadge: {
    display: 'inline-block',
    backgroundColor: '#fff3e0',
    color: '#e65100',
    borderRadius: 10,
    padding: '1px 8px',
    fontSize: '0.75rem',
    fontWeight: 700,
  },
  historyLabel: {
    fontSize: '0.72rem',
    color: '#9e9e9e',
    fontStyle: 'italic',
    paddingLeft: 8,
  },
  collapseCell: {
    padding: 0,
    borderBottom: 'none',
  },
  innerTable: {
    width: '100%',
  },
  innerTableRow: {
    borderTop: '1px dashed #e0e0e0',
  },
}));

// ─── Helper: group samples by docket, sorted by testNo desc ────────────────
const groupByDocket = samples => {
  const map = {};
  samples.forEach(s => {
    const key = s.wH_DocketNumber;
    if (!map[key]) map[key] = [];
    map[key].push(s);
  });
  // Sort each group: highest testNo first (latest at top)
  Object.values(map).forEach(arr => arr.sort((a, b) => (b.testNo ?? 0) - (a.testNo ?? 0)));
  return map;
};

// ─── Format a date value using the existing dateTemplate util ───────────────
const formatDate = val => {
  if (!val) return '—';
  try {
    return new moment(val).format('DD/MM/YYYY, h:mm');
  } catch {
    return val;
  }
};

// ─── Normalize a field value for filter-list / comparison purposes ─────────
const displayValue = (row, key) => {
  const v = row?.[key];
  return v === null || v === undefined || v === '' ? '—' : String(v);
};

// ─── Compare two rows on a given key (numeric / date / string aware) ───────
const compareRows = (a, b, key) => {
  let av = a?.[key];
  let bv = b?.[key];

  if (key === 'finishDate') {
    av = av ? new Date(av).getTime() : -Infinity;
    bv = bv ? new Date(bv).getTime() : -Infinity;
    return av - bv;
  }
  if (typeof av === 'number' || typeof bv === 'number') {
    av = av ?? -Infinity;
    bv = bv ?? -Infinity;
    return av - bv;
  }
  av = (av ?? '').toString().toLowerCase();
  bv = (bv ?? '').toString().toLowerCase();
  return av.localeCompare(bv);
};

// Columns that get an Excel-style checkbox filter dropdown
const FILTERABLE_KEYS = ['customer', 'sampleHandler', 'sP_Number', 'testNo'];

export const GroupedSampleTable = ({ samples, isRetest, onActionClick, onMarkRetest, searchTerm }) => {
  const classes = useStyles();
  const [expandedDockets, setExpandedDockets] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [sort, setSort] = useState({ key: null, direction: 'asc' });
  const [columnFilters, setColumnFilters] = useState({}); // { customer: Set(['Bord']), ... }
  const [filterMenu, setFilterMenu] = useState({ anchorEl: null, columnKey: null });

  const grouped = useMemo(() => groupByDocket(samples), [samples]);

  // Unique values per filterable column, built off the latest (primary) row per docket
  const uniqueValues = useMemo(() => {
    const collect = key => {
      const set = new Set();
      Object.values(grouped).forEach(rows => set.add(displayValue(rows[0], key)));
      return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    };
    return FILTERABLE_KEYS.reduce((acc, key) => ({ ...acc, [key]: collect(key) }), {});
  }, [grouped]);

  const filteredDockets = useMemo(() => {
    let entries = Object.entries(grouped);

    // Default order: latest docket first
    entries.sort((a, b) => Number(b[0]) - Number(a[0]));

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      entries = entries.filter(
        ([docket, rows]) =>
          docket.toString().includes(lower) ||
          rows.some(
            r =>
              (r.customer ?? '').toLowerCase().includes(lower) ||
              (r.sP_Number ?? '').toLowerCase().includes(lower) ||
              (r.sampleHandler ?? '').toLowerCase().includes(lower),
          ),
      );
    }

    // Column filters — a docket group matches if its latest row's value is in the selected set
    Object.entries(columnFilters).forEach(([key, selected]) => {
      if (selected && selected.size > 0) {
        entries = entries.filter(([, rows]) => selected.has(displayValue(rows[0], key)));
      }
    });

    // Column sort — overrides the default docket-desc order when active
    if (sort.key) {
      entries = [...entries].sort((a, b) => {
        const result = compareRows(a[1][0], b[1][0], sort.key);
        return sort.direction === 'asc' ? result : -result;
      });
    }

    return entries;
  }, [grouped, searchTerm, columnFilters, sort]);

  const paginatedDockets = filteredDockets?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const toggleDocket = docket => setExpandedDockets(prev => ({ ...prev, [docket]: !prev[docket] }));

  const handleSort = key => {
    setPage(0);
    setSort(prev => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: 'asc' }; // 3rd click clears back to default order
    });
  };

  const openFilterMenu = (e, key) => setFilterMenu({ anchorEl: e.currentTarget, columnKey: key });
  const closeFilterMenu = () => setFilterMenu({ anchorEl: null, columnKey: null });

  const toggleFilterValue = (key, value) => {
    setPage(0);
    setColumnFilters(prev => {
      const current = new Set(prev[key] ?? []);
      if (current.has(value)) current.delete(value);
      else current.add(value);
      return { ...prev, [key]: current };
    });
  };

  const selectAllColumn = key => {
    setPage(0);
    setColumnFilters(prev => ({ ...prev, [key]: new Set(uniqueValues[key]) }));
  };

  const clearColumnFilter = key => {
    setPage(0);
    setColumnFilters(prev => ({ ...prev, [key]: new Set() }));
  };

  // Shared cell style — history rows override color/opacity via `isHistory`
  const cell = (isHistory = false) => ({
    fontSize: isHistory ? '0.79rem' : '0.82rem',
    padding: '6px 10px',
    color: isHistory ? '#888' : 'inherit',
    borderBottom: isHistory ? '1px solid #f0f0f0' : '1px solid #e8e8e8',
    whiteSpace: 'nowrap',
  });

  // Renders a header cell with sort (always) and an optional filter icon
  const renderHeader = (key, label, { align = 'left', filterable = false } = {}) => {
    const isFiltered = (columnFilters[key]?.size ?? 0) > 0;
    const justify = align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start';

    return (
      <TableCell className={classes.tableHeadCell} style={{ textAlign: align }}>
        <div className={classes.headerInner}>
          <div>{label}</div>

          <div className={classes.headerIcons}>
            <TableSortLabel
              active={sort.key === key}
              direction={sort.key === key ? sort.direction : 'asc'}
              onClick={() => handleSort(key)}
            />

            {filterable && (
              <IconButton size="small" className={classes.filterBtn} onClick={e => openFilterMenu(e, key)}>
                <Badge color="primary" variant="dot" invisible={!isFiltered}>
                  <FilterListIcon
                    style={{
                      fontSize: 14,
                      color: isFiltered ? '#1976d2' : '#bdbdbd',
                    }}
                  />
                </Badge>
              </IconButton>
            )}
          </div>
        </div>
      </TableCell>
    );
  };

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      {/* colgroup fixes every column to an explicit width — the single source of truth */}
      <Table size="small" stickyHeader style={{ tableLayout: 'fixed', minWidth: 1400 }}>
        <colgroup>
          <col style={{ width: 30 }} /> {/* expand */}
          <col style={{ width: 85 }} /> {/* WH docket */}
          <col style={{ width: 90 }} /> {/* Customer docket */}
          <col style={{ width: 100 }} /> {/* Customer */}
          <col style={{ width: 70 }} /> {/* SP Number */}
          <col style={{ width: 100 }} /> {/* Handler */}
          <col style={{ width: 95 }} /> {/* Customer MC */}
          <col style={{ width: 65 }} /> {/* MC */}
          <col style={{ width: 80 }} /> {/* Start Wt */}
          <col style={{ width: 80 }} /> {/* Finish Wt */}
          <col style={{ width: 90 }} /> {/* Customer Net Wt */}
          <col style={{ width: 90 }} /> {/* Customer Value */}
          <col style={{ width: 105 }} /> {/* Finish Date */}
          <col style={{ width: 75 }} /> {/* Cook Hrs */}
          <col style={{ width: 70 }} /> {/* Test # */}
          <col style={{ width: 120 }} /> {/* Action */}
        </colgroup>

        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHeadCell} />
            {renderHeader('wH_DocketNumber', 'WH Docket #')}
            {renderHeader('customerDocketNumber', 'Customer Docket #')}
            {renderHeader('customer', 'Customer', { filterable: true })}
            {renderHeader('sP_Number', 'SP Number', { filterable: true })}
            {renderHeader('sampleHandler', 'Handler', { filterable: true })}
            {renderHeader('customerMC', 'Customer MC %', { align: 'right' })}
            {renderHeader('mC_Percent', 'MC %', { align: 'right' })}
            {renderHeader('startingWeight', 'Start Wt', { align: 'right' })}
            {renderHeader('finishWeight', 'Finish Wt', { align: 'right' })}
            {renderHeader('customerNetWeight', 'Customer Net Wt', { align: 'right' })}
            {renderHeader('customerValueInEuros', 'Customer Value', { align: 'right' })}
            {renderHeader('finishDate', 'Finish Date')}
            {renderHeader('cookTimeHours', 'Cook Hrs', { align: 'right' })}
            {renderHeader('testNo', 'Test #', { align: 'center', filterable: true })}
            <TableCell className={classes.tableHeadCell} style={{ textAlign: 'center' }}>
              Action
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {filteredDockets.length === 0 && (
            <TableRow>
              <TableCell colSpan={16} style={{ textAlign: 'center', padding: 32, color: '#9e9e9e' }}>
                No records found.
              </TableCell>
            </TableRow>
          )}

          {paginatedDockets.map(([docket, rows]) => {
            const latest = rows[0];
            const hasHistory = rows.length > 1;
            const isExpanded = !!expandedDockets[docket];
            const primaryBg = isRetest ? '#fff8e1' : '#fff';
            const primaryHover = isRetest ? '#fff3cd' : '#f5f9ff';
            const historyBg = isRetest ? '#fffdf0' : '#fafafa';

            return (
              <React.Fragment key={docket}>
                {/* ── PRIMARY ROW ── */}
                <TableRow
                  style={{ backgroundColor: primaryBg, cursor: 'default' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = primaryHover;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = primaryBg;
                  }}>
                  {/* Expand toggle */}
                  <TableCell style={{ ...cell(), padding: '0 4px', textAlign: 'center' }}>
                    {hasHistory && (
                      <Tooltip title={isExpanded ? 'Collapse' : `${rows.length - 1} older test(s)`}>
                        <IconButton size="small" onClick={() => toggleDocket(docket)} style={{ padding: 2 }}>
                          {isExpanded ? (
                            <KeyboardArrowDownIcon style={{ fontSize: 16, color: '#1976d2' }} />
                          ) : (
                            <KeyboardArrowRightIcon style={{ fontSize: 16, color: '#bdbdbd' }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>

                  {/* Docket # */}
                  <TableCell style={cell()}>
                    <Button
                      className={classes.docketBtn}
                      sx={{ marginRight: 0 }}
                      onClick={() =>
                        window.open(`https://logwatch.myreports.online/dockets?docket=${latest.wH_DocketNumber}`, '_blank')
                      }>
                      {latest.wH_DocketNumber}
                    </Button>
                    {hasHistory && (
                      <span style={{ fontSize: '0.7rem', color: '#aaa', marginLeft: 0 }}>{rows.length} tests</span>
                    )}
                  </TableCell>
                  <TableCell style={cell()}>{latest.customerDocketNumber ?? '—'}</TableCell>
                  <TableCell style={cell()}>{latest.customer ?? '—'}</TableCell>
                  <TableCell style={cell()}>{latest.sP_Number ?? '—'}</TableCell>
                  <TableCell style={cell()}>{latest.sampleHandler ?? '—'}</TableCell>
                  <TableCell style={{ ...cell(), textAlign: 'right' }}>{latest.customerMC ?? '—'}</TableCell>
                  <TableCell style={{ ...cell(), textAlign: 'right' }}>{latest.mC_Percent ?? '—'}</TableCell>
                  <TableCell style={{ ...cell(), textAlign: 'right' }}>{latest.startingWeight ?? '—'}</TableCell>
                  <TableCell style={{ ...cell(), textAlign: 'right' }}>{latest.finishWeight ?? '—'}</TableCell>
                  <TableCell style={{ ...cell(), textAlign: 'right' }}>{latest.customerNetWeight ?? '—'}</TableCell>
                  <TableCell style={{ ...cell(), textAlign: 'right' }}>{latest.customerValueInEuros ?? '—'}</TableCell>
                  <TableCell style={cell()}>{formatDate(latest.finishDate)}</TableCell>
                  <TableCell style={{ ...cell(), textAlign: 'right' }}>{latest.cookTimeHours ?? '—'}</TableCell>

                  {/* Test # badge */}
                  <TableCell style={{ ...cell(), textAlign: 'center' }}>
                    <span className={isRetest ? classes.retestNoBadge : classes.testNoBadge}>#{latest.testNo ?? 1}</span>
                  </TableCell>

                  {/* Action */}
                  <TableCell style={{ ...cell(), textAlign: 'center' }}>
                    {isRetest ? (
                      <Button
                        size="small"
                        variant="contained"
                        style={{ fontWeight: 600, backgroundColor: '#f57c00', color: '#fff', fontSize: '0.75rem' }}
                        onClick={() => onActionClick(latest)}>
                        Retest Sample
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        style={{ fontWeight: 600, color: '#f57c00', borderColor: '#f57c00', fontSize: '0.75rem' }}
                        onClick={() => onMarkRetest(latest)}>
                        Mark for Retest
                      </Button>
                    )}
                  </TableCell>
                </TableRow>

                {/* ── HISTORY ROWS — same column structure, just dimmed ── */}
                {hasHistory &&
                  isExpanded &&
                  rows.slice(1).map(histRow => (
                    <TableRow key={`${docket}-hist-${histRow.id}`} style={{ backgroundColor: historyBg }}>
                      {/* No toggle in expand col — just a visual indent line */}
                      <TableCell
                        style={{ ...cell(true), padding: 0, borderLeft: '3px solid #e3f2fd', borderRight: 'none' }}
                      />

                      {/* Docket col: show SP number as the meaningful differentiator, docket is redundant */}
                      <TableCell style={{ ...cell(true), paddingLeft: 14, color: '#bbb', fontSize: '0.75rem' }}>
                        {histRow.wH_DocketNumber}
                      </TableCell>

                      <TableCell style={cell(true)}>{histRow.customerDocketNumber ?? '—'}</TableCell>
                      <TableCell style={cell(true)}>{histRow.customer ?? '—'}</TableCell>
                      <TableCell style={cell(true)}>{histRow.sP_Number ?? '—'}</TableCell>
                      <TableCell style={cell(true)}>{histRow.sampleHandler ?? '—'}</TableCell>
                      <TableCell style={{ ...cell(true), textAlign: 'right' }}>{histRow.customerMC ?? '—'}</TableCell>
                      <TableCell style={{ ...cell(true), textAlign: 'right' }}>{histRow.mC_Percent ?? '—'}</TableCell>
                      <TableCell style={{ ...cell(true), textAlign: 'right' }}>{histRow.startingWeight ?? '—'}</TableCell>
                      <TableCell style={{ ...cell(true), textAlign: 'right' }}>{histRow.finishWeight ?? '—'}</TableCell>
                      <TableCell style={{ ...cell(true), textAlign: 'right' }}>{histRow.customerNetWeight ?? '—'}</TableCell>
                      <TableCell style={{ ...cell(true), textAlign: 'right' }}>
                        {histRow.customerValueInEuros ?? '—'}
                      </TableCell>
                      <TableCell style={cell(true)}>{formatDate(histRow.finishDate)}</TableCell>
                      <TableCell style={{ ...cell(true), textAlign: 'right' }}>{histRow.cookTimeHours ?? '—'}</TableCell>
                      <TableCell style={{ ...cell(true), textAlign: 'center' }}>
                        <span className={classes.testNoBadgeOld}>#{histRow.testNo ?? '—'}</span>
                      </TableCell>
                      {/* No action on history rows */}
                      <TableCell style={cell(true)} />
                    </TableRow>
                  ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={filteredDockets.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Excel-style filter dropdown — checkbox list per filterable column */}
      <Menu
        anchorEl={filterMenu.anchorEl}
        open={Boolean(filterMenu.anchorEl)}
        onClose={closeFilterMenu}
        PaperProps={{ style: { maxHeight: 320, width: 220 } }}>
        {filterMenu.columnKey && (
          <>
            <MenuItem dense onClick={() => selectAllColumn(filterMenu.columnKey)}>
              <ListItemText primary="Select All" />
            </MenuItem>
            <MenuItem dense onClick={() => clearColumnFilter(filterMenu.columnKey)}>
              <ListItemText primary="Clear Filter" />
            </MenuItem>
            <Divider />
            {(uniqueValues[filterMenu.columnKey] ?? []).map(value => (
              <MenuItem key={value} dense onClick={() => toggleFilterValue(filterMenu.columnKey, value)}>
                <Checkbox
                  size="small"
                  checked={(columnFilters[filterMenu.columnKey] ?? new Set()).has(value)}
                  style={{ padding: 4 }}
                />
                <ListItemText primary={value} primaryTypographyProps={{ style: { fontSize: '0.82rem' } }} />
              </MenuItem>
            ))}
          </>
        )}
      </Menu>
    </TableContainer>
  );
};
